import { Container, Graphics } from "../../../vendor/pixi.min.mjs";

/**
 * Lightweight confetti-style particle burst played at the balloon's
 * position when a pickup is collected or an ability fires.
 *
 * Lives inside the Frogger world container so the particles automatically
 * scroll with the camera and stay glued to the spot the burst happened
 * (no canvas/window coord conversion needed).
 *
 * Per-kind profiles tune the palette, count, size, direction cone, gravity,
 * speed and lifetime, so each pickup feels distinct:
 *   • `coin`     — small gold/amber upward spray
 *   • `star`     — pink/violet upward spray, slightly punchier
 *   • `bomb`     — large 360° red explosion (an entire row is destroyed)
 *   • `dash`     — narrow cyan exhaust plume falling downward
 *   • `slowtime` — slow, ambient violet drift outward
 *
 * Particles are pooled across bursts so a long combo of pickups never
 * allocates a new wave of Graphics objects per frame.
 */

const PI = Math.PI;
const UP = -PI / 2;
const DOWN = PI / 2;

const COIN_PALETTE = [0xfbbf24, 0xf59e0b, 0xfde68a, 0xfff6c8, 0xffd24a];
const STAR_PALETTE = [0xec4899, 0xf472b6, 0xfbcfe8, 0xffffff, 0xa855f7];
// Red explosion palette used when the bomb detonates and cars are destroyed.
const BOMB_PALETTE = [0xef4444, 0xdc2626, 0xb91c1c, 0xf97316, 0xfde047];
// Hot-to-cool fuse palette used by the idle dynamite icon (white-hot tip
// fading down through cream / yellow / amber). Kept separate from the
// explosion palette so each can be tuned independently.
const FUSE_SPARK_PALETTE = [0xffffff, 0xfef9c3, 0xfde047, 0xfbbf24, 0xf97316];
const DASH_PALETTE = [0x06b6d4, 0x0ea5e9, 0x38bdf8, 0x60a5fa, 0xe0f2fe];
const SLOWTIME_PALETTE = [0x8b5cf6, 0xa855f7, 0xc084fc, 0xddd6fe, 0xffffff];
/**
 * Hot red/orange/yellow palette used by Snake's Speed+ pickup pop and
 * its boost trail — reads as a turbo nitro / fireworks explosion.
 */
const SPEED_PALETTE = [0xef4444, 0xf97316, 0xfbbf24, 0xfde047, 0xffffff, 0xdc2626];
/**
 * Full-rainbow palette for Snake's Chaos (jolly-joker) wipe outcome —
 * fired once per destroyed obstacle so each pop comes out in a
 * different colour and the wipe feels confetti-storm rainbow.
 */
const CHAOS_PALETTE = [
  0xef4444, 0xf97316, 0xfbbf24, 0x22c55e,
  0x06b6d4, 0x3b82f6, 0xa855f7, 0xec4899, 0xffffff,
];

/**
 * Purple + white palette for the Chaos PICKUP pop (separate from the
 * rainbow wipe-explosion palette above). The pickup is a smaller, more
 * focused fanfare at the snake head — it identifies "chaos perk
 * collected" via colour rather than via volume.
 */
const CHAOS_POP_PALETTE = [
  0xffffff, 0xf5f3ff, 0xe9d5ff, 0xc4b5fd,
  0xa855f7, 0x8b5cf6, 0x7c3aed, 0x6d28d9,
];
/** Fallback feather palette for birds with no recognised colour tag. */
const BIRD_FEATHER_PALETTE = [0xfca5a5, 0xfecaca, 0xfee2e2, 0xffffff, 0xf3f4f6];
/** Red palette matching the balloon, used for its movement-inertia trail. */
const BALLOON_TRAIL_PALETTE = [0xef4444, 0xdc2626, 0xfca5a5, 0xfee2e2, 0xffffff];

/**
 * @typedef {Object} BurstProfile
 * @property {number[]} palette
 * @property {number} count          — particles per burst
 * @property {number} size           — base square edge in px
 * @property {number} life           — frames the particles live
 * @property {number} speedMin
 * @property {number} speedMax
 * @property {number} angleCenter    — middle of the emission cone (rad)
 * @property {number} angleSpread    — full width of the cone (rad)
 * @property {number} gravity        — px/frame² added to vy (+y = down)
 * @property {number} drag           — per-frame velocity multiplier
 */

/** @type {Record<string, BurstProfile>} */
const PROFILES = {
  coin: {
    palette: COIN_PALETTE,
    count: 16,
    size: 11,
    life: 42,
    speedMin: 2.0,
    speedMax: 5.0,
    angleCenter: UP,
    angleSpread: PI * 1.4,
    gravity: 0.18,
    drag: 0.94,
  },
  star: {
    palette: STAR_PALETTE,
    count: 20,
    size: 12,
    life: 50,
    speedMin: 2.2,
    speedMax: 5.6,
    angleCenter: UP,
    angleSpread: PI * 1.5,
    gravity: 0.18,
    drag: 0.94,
  },
  // One-shot fuse sputter — a single tiny spark emitted from a bomb
  // pickup icon every few frames so the bomb idles like a lit fuse
  // before being collected. FroggerAbilities calls this on a per-item
  // timer; particles last ~0.17 s and barely move so they cluster at
  // the fuse tip and instantly fade.
  fuseSpark: {
    palette: FUSE_SPARK_PALETTE,
    count: 1,
    size: 2,
    life: 10,
    speedMin: 0.4,
    speedMax: 1.6,
    angleCenter: UP,
    angleSpread: PI * 0.7,
    gravity: 0.10,
    drag: 0.88,
  },
  // Big red detonation pop at each destroyed vehicle. Fired by both the
  // bomb ability (one per cleared car in BOMB_RADIUS rows) and the dash
  // ability (one per cleared car along the traversed lanes), so the
  // explosions visibly track the spots cars disappeared from.
  bomb: {
    palette: BOMB_PALETTE,
    count: 40,
    size: 12,
    life: 55,
    speedMin: 10.0,
    speedMax: 20.0,
    angleCenter: UP,
    angleSpread: PI * 2,
    gravity: 0.2,
    drag: 0.95,
  },
  dash: {
    palette: DASH_PALETTE,
    count: 20,
    size: 10,
    life: 42,
    speedMin: 2.2,
    speedMax: 5.2,
    angleCenter: DOWN,
    angleSpread: PI * 0.85,
    gravity: 0.32,
    drag: 0.95,
  },
  slowtime: {
    palette: SLOWTIME_PALETTE,
    count: 22,
    size: 11,
    life: 60,
    speedMin: 1.4,
    speedMax: 3.6,
    angleCenter: UP,
    angleSpread: PI * 2,
    gravity: 0.06,
    drag: 0.93,
  },
  // Tiny feather burst emitted behind each bird while in flight. The
  // palette here is just a fallback — FroggerBirds passes a per-bird
  // palette override to {@link burst} so red birds shed red feathers,
  // purple birds shed purple feathers, etc. Two particles per emission
  // fired in a full 360° fan, so feathers scatter in every direction
  // instead of leaving a tight line behind the bird. Light gravity
  // pulls them softly down once their initial velocity bleeds off.
  // Red puff dropped at the balloon's previous cell every time the
  // player moves — reads as an inertia wake behind the balloon. Short
  // life and modest count keep the trail readable without flooding the
  // screen during fast input.
  balloonTrail: {
    palette: BALLOON_TRAIL_PALETTE,
    count: 8,
    size: 4,
    life: 32,
    speedMin: 0.4,
    speedMax: 1.8,
    angleCenter: DOWN,
    angleSpread: PI * 2,
    gravity: 0.10,
    drag: 0.92,
  },
  birdTrail: {
    palette: BIRD_FEATHER_PALETTE,
    count: 3,
    size: 4,
    life: 42,
    speedMin: 0.5,
    speedMax: 2.0,
    angleCenter: DOWN,
    angleSpread: PI * 2,
    gravity: 0.05,
    drag: 0.93,
  },
  // Aggressive Speed+ pickup pop — fired once on collection. Big, hot,
  // 360° fireworks burst at the snake head. Bigger and longer-lived
  // than `bomb` so the moment of pickup reads as a turbo nitro.
  speedPop: {
    palette: SPEED_PALETTE,
    count: 70,
    size: 16,
    life: 70,
    speedMin: 8.0,
    speedMax: 22.0,
    angleCenter: UP,
    angleSpread: PI * 2,
    gravity: 0.18,
    drag: 0.95,
  },
  // Per-tick boost trail emitted from the cell the head just left while
  // Speed+ is active. Denser and chunkier than `balloonTrail` so the
  // wake reads as a thick exhaust plume rather than a light puff.
  speedTrail: {
    palette: SPEED_PALETTE,
    count: 18,
    size: 8,
    life: 42,
    speedMin: 1.2,
    speedMax: 4.4,
    angleCenter: UP,
    angleSpread: PI * 2,
    gravity: 0.10,
    drag: 0.93,
  },
  // Celebratory purple/white pop fired at the Chaos perk pickup spot.
  // Smaller and tighter than the rainbow `chaosBomb` (which fires per
  // obstacle during the wipe outcome) so the pickup reads as a focused
  // "you got chaos" fanfare rather than overpowering the screen.
  chaosPop: {
    palette: CHAOS_POP_PALETTE,
    count: 32,
    size: 9,
    life: 55,
    speedMin: 4.0,
    speedMax: 11.0,
    angleCenter: UP,
    angleSpread: PI * 2,
    gravity: 0.16,
    drag: 0.94,
  },
  // Per-obstacle explosion during Chaos "wipe" — fired potentially many
  // times in a row, so it's deliberately lighter than `bomb`/`chaosPop`
  // but still rainbow-coloured to stay on-theme.
  chaosBomb: {
    palette: CHAOS_PALETTE,
    count: 26,
    size: 11,
    life: 50,
    speedMin: 4.0,
    speedMax: 13.0,
    angleCenter: UP,
    angleSpread: PI * 2,
    gravity: 0.18,
    drag: 0.94,
  },
  // Snake-only — obstacle smash burst fired while the Speed+ boost is
  // active and the destroy-obstacles toggle is on. Same chunky 360°
  // shape as `bomb` but in cool blue/white so the smash visually reads
  // as "speed shockwave" instead of "red explosion" (those reds were
  // visually conflicting with the Speed+ pickup pop / trail palette).
  speedSmash: {
    palette: [0xffffff, 0xf0f9ff, 0xdbeafe, 0xbfdbfe, 0x93c5fd, 0x60a5fa, 0x3b82f6],
    count: 40,
    size: 12,
    life: 55,
    speedMin: 10.0,
    speedMax: 20.0,
    angleCenter: UP,
    angleSpread: PI * 2,
    gravity: 0.2,
    drag: 0.95,
  },
  // Snake-only — fires when a tram cart is popped off the tail (shrink
  // perk, Chaos shrink outcome). One burst per cart removed, centred on
  // the cart's last position so the player sees the cart visibly EXPLODE
  // out of existence instead of just silently vanishing.
  cartPop: {
    palette: CHAOS_PALETTE,
    count: 34,
    size: 12,
    life: 48,
    speedMin: 4.0,
    speedMax: 12.0,
    angleCenter: UP,
    angleSpread: PI * 2,
    gravity: 0.18,
    drag: 0.94,
  },
  // Snake-only — single chunky blue/white EXPLOSION fired at the
  // station's centre the moment it arrives. Continuous "I'm here, pick
  // me up!" signalling is handled separately by the station's gentle
  // breathing pulse (SnakeStations.pulse) — so this burst doesn't need
  // to outline the station, just announce its arrival with a punch.
  // Reach ≈ 2 cells radius, big enough to read over a 4×2 station
  // without overflowing into other gameplay elements.
  stationAppear: {
    palette: [
      0xffffff, 0xf0f9ff, 0xdbeafe, 0xbfdbfe,
      0x93c5fd, 0x60a5fa, 0x3b82f6, 0x1d4ed8,
    ],
    count: 56,
    size: 11,
    life: 50,
    speedMin: 4.0,
    speedMax: 9.5,
    angleCenter: UP,
    angleSpread: PI * 2,
    gravity: 0.10,
    drag: 0.93,
  },
};

const ROTATION_MIN = -0.18;
const ROTATION_MAX = 0.18;
const DEFAULT_KIND = "coin";

export class FroggerPickupBurst {
  /** Add this to the world container so particles scroll with the camera. */
  container = new Container();

  /** @type {Array<{gfx: Graphics, vx: number, vy: number, life: number, maxLife: number, spin: number, gravity: number, drag: number}>} */
  _active = [];
  /** @type {Graphics[]} */
  _pool = [];

  /**
   * Spawn a burst centered at (worldX, worldY). The `kind` selects a
   * profile from {@link PROFILES}; unknown kinds fall back to `coin`.
   *
   * `paletteOverride` (non-empty array of 24-bit RGB ints) replaces the
   * profile's palette for this call only — used by birds so each one
   * drops feathers in its own colour.
   *
   * `velocityBias` (`{x, y}` in px/frame) is added to every particle's
   * initial velocity, so the whole cloud drifts together while particles
   * still scatter around that drift. Used by the balloon's inertia
   * trail to drag the wake toward the cell the player is heading into.
   *
   * @param {number} worldX
   * @param {number} worldY
   * @param {'coin'|'star'|'bomb'|'dash'|'slowtime'|'birdTrail'|'balloonTrail'|'fuseSpark'|'speedPop'|'speedTrail'|'speedSmash'|'chaosPop'|'chaosBomb'|'cartPop'|'stationAppear'} [kind]
   * @param {number[]|null} [paletteOverride]
   * @param {{x:number, y:number}|null} [velocityBias]
   */
  burst(worldX, worldY, kind = DEFAULT_KIND, paletteOverride = null, velocityBias = null) {
    const p = PROFILES[kind] || PROFILES[DEFAULT_KIND];
    const palette = paletteOverride && paletteOverride.length
      ? paletteOverride
      : p.palette;
    const bvx = velocityBias?.x ?? 0;
    const bvy = velocityBias?.y ?? 0;
    const halfSpread = p.angleSpread / 2;

    for (let i = 0; i < p.count; i++) {
      const gfx = this._acquireGraphic();
      const color = palette[Math.floor(Math.random() * palette.length)];

      gfx.clear();
      const size = p.size * (0.7 + Math.random() * 0.7);
      gfx.rect(-size / 2, -size / 2, size, size);
      gfx.fill(color);

      gfx.x = worldX;
      gfx.y = worldY;
      gfx.alpha = 1;
      gfx.rotation = Math.random() * PI * 2;
      gfx.scale.set(1);

      const angle =
        p.angleCenter +
        (Math.random() - 0.5) *
          (p.angleSpread > 0 ? p.angleSpread : halfSpread);
      const speed = p.speedMin + Math.random() * (p.speedMax - p.speedMin);

      this._active.push({
        gfx,
        vx: Math.cos(angle) * speed + (Math.random() - 0.5) * 0.6 + bvx,
        vy: Math.sin(angle) * speed + bvy,
        life: p.life * (0.85 + Math.random() * 0.3),
        maxLife: p.life,
        spin: ROTATION_MIN + Math.random() * (ROTATION_MAX - ROTATION_MIN),
        gravity: p.gravity,
        drag: p.drag,
      });

      this.container.addChild(gfx);
    }
  }

  /** Advance every active particle by one frame. */
  update(delta) {
    if (this._active.length === 0) return;

    for (let i = this._active.length - 1; i >= 0; i--) {
      const p = this._active[i];
      p.vx *= p.drag;
      p.vy = p.vy * p.drag + p.gravity * delta;
      p.gfx.x += p.vx * delta;
      p.gfx.y += p.vy * delta;
      p.gfx.rotation += p.spin * delta;
      p.life -= delta;

      const t = Math.max(0, p.life / p.maxLife);
      p.gfx.alpha = t;
      p.gfx.scale.set(0.4 + 0.6 * t);

      if (p.life <= 0) {
        this.container.removeChild(p.gfx);
        this._pool.push(p.gfx);
        this._active.splice(i, 1);
      }
    }
  }

  destroy() {
    for (const p of this._active) p.gfx.destroy();
    for (const g of this._pool) g.destroy();
    this._active = [];
    this._pool = [];
    this.container.removeChildren();
  }

  _acquireGraphic() {
    return this._pool.pop() || new Graphics();
  }
}
